import { Track } from "@/src/lib/tracking";
import { formatNumberWithZeroCount, getLeftDigits } from "utils";
import { ICreateForecastLineChartParams, IDrawEndCallback } from "./type";
import { createDualYAxisLineChart } from "./createDualYAxisLineChart";
import { createOrSelectGroup, getSVG, getTransition } from "./utils";
import {
  buildSubscriptNumberHtml,
  formatNumberToHtml,
} from "@utils/numberUtils";
import { formatBigNumber } from "@utils/formatBigNumber";

const getForecastPriceHtml = (
  price: number,
  type: "stock" | "token",
  zeroClass?: string,
) => {
  if (type === "stock") {
    if (price > 1) {
      return formatNumberToHtml(
        formatBigNumber(price, { precision: 2, trimZeros: true }),
        zeroClass,
      );
    }
    return formatNumberToHtml(
      formatBigNumber(price, { precision: 4, trimZeros: true }),
      zeroClass,
    );
  } else {
    const priceData = formatNumberWithZeroCount({
      numStr: price.toString(),
      decimals: 6,
      minZeroCount: 6,
    });
    const leftDigits = getLeftDigits(priceData);
    return buildSubscriptNumberHtml({
      integerPart: priceData.integerPart,
      zeroCount: priceData.zeroCount,
      restOfDecimal: leftDigits,
    });
  }
};

export const createForecastLineChart = (
  params: ICreateForecastLineChartParams,
) => {
  if (!window.d3?.select) {
    Track.log(
      "warn",
      "createForecastLineChart",
      "d3 is unavailable, skip drawing",
    );
    return;
  }
  const XAxis = [...params.XAxis];
  const interval = Math.floor((params.XAxis.length / 5) * 2);
  let i = 0;
  while (i < interval) {
    XAxis.push({ x: params.XAxis.length + i, label: "" });
    i++;
  }
  XAxis.push({
    x: params.XAxis.length + i,
    label: params.forecastTimeLabel || "",
  });

  const ticks = [...params.ticks, XAxis.at(-1)!];
  let right = 0;

  const forecastPriceClass =
    "price flex-inline absolute justify-center items-center py-0.5 px-1 rounded-1 body_bold_xs right-0";
  const zeroCountClass = "inline-block text-12px transform scale-[0.6333]";
  let forecastData: {
    type: "low" | "avg" | "high";
    value: number;
    html: string;
  }[] = [];
  if (params.forecast) {
    const svg = getSVG(params.config.el);
    if (!svg) {
      return;
    }
    forecastData = [];
    Object.entries(params.forecast).forEach(([key, d]) => {
      // const price = getForecastPriceHtml(d, params.type || "stock");
      const html = getForecastPriceHtml(
        d,
        params.type || "stock",
        zeroCountClass,
      );
      const el = window.d3.select(params.config.el);
      forecastData.push({
        type: key as "low" | "avg" | "high",
        value: d,
        html: html || "",
      });
      if (!html) {
        return;
      }
      const groupEl = el
        .append("p")
        .attr("class", forecastPriceClass)
        .html(html);
      const node = groupEl.node();
      const width = node?.getBoundingClientRect()?.width;
      groupEl.remove();
      right = Math.max(right, width || 0);
    });
    // 测量右侧将要显示的文本宽度
    // right = getMaxTextWidth(Object.values(params.forecast).map((d) => d.toString()), svg);
    svg.remove();
    right = right + 14;
  }
  const drawEndCallback: IDrawEndCallback = ({ svg, xScale, yScale }) => {
    if (!params.config.el) {
      return;
    }
    const forecastGroup = createOrSelectGroup(svg, "forecast");
    const el = window.d3.select(params.config.el);
    forecastGroup.selectAll("*").remove();
    const t = getTransition();
    // 点
    const point = forecastGroup
      .selectAll<SVGCircleElement, number>(".point")
      .data([params.YAxis.data.at(-1)!]);
    point.exit().transition(t).attr("opacity", 0).remove();
    const enterPoint = point
      .enter()
      .append("circle")
      .attr("class", "point")
      .attr("r", 3)
      .style("fill", params.YAxis.color)
      .style("opacity", 0);
    point
      .merge(enterPoint)
      .transition(t)
      .attr("cx", xScale(params.YAxis.data.length - 1))
      .attr("cy", yScale(params.YAxis.data.at(-1)!))
      .style("opacity", 1);
    if (params.showMemberMask) {
      const memberMask = el
        .selectAll<HTMLDivElement, null>(".memberMask")
        .data([null]);
      memberMask.exit().transition(t).style("opacity", 0).remove();
      const enterMemberMask = memberMask
        .enter()
        .append("div")
        .html(params.memberMask!)
        .attr("class", "memberMask absolute top-0 right-0 bottom-9")
        .style("opacity", 0);
      enterMemberMask
        .merge(memberMask)
        .transition(t)
        .style("left", () => {
          if (window.matchMedia("(max-width: 960px)").matches) {
            return "50%";
          }
          return `${xScale(params.YAxis.data.length - 1) + 4}px`;
        })
        .style("opacity", 1);
      enterMemberMask.select(".button").on("click", (event: MouseEvent) => {
        event.stopPropagation();
        params.onLogin?.(event);
      });
      return;
    }
    if (!Object.values(params.forecast || {}).length) {
      return;
    }
    el.selectAll(".memberMask").selectAll("*").remove();
    // 线
    const line = forecastGroup
      .selectAll<SVGLineElement, number>(".line")
      .data([params.forecast!.avg]);
    line.exit().transition(t).attr("opacity", 0).remove();
    const enterLine = line
      .enter()
      .append("line")
      .attr("class", "line")
      .style("stroke", "var(--edgen-background_white_overlay40)")
      .style("stroke-width", 1)
      .style("stroke-dasharray", "3,3")
      .style("opacity", 0);
    line
      .merge(enterLine)
      .transition(t)
      .attr("x1", xScale(params.YAxis.data.length - 1))
      .attr("y1", yScale(params.YAxis.data.at(-1)!))
      .attr("x2", xScale(XAxis.length - 1))
      .attr("y2", yScale(params.forecast!.avg))
      .style("opacity", 1);

    // 1. 定义一个 "line" 生成器，它能将坐标点数组转换成 SVG 路径字符串
    const lineGenerator = window.d3
      .line<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));

    // 上涨面积图
    const highPoints = [
      { x: params.YAxis.data.length - 1, y: params.YAxis.data.at(-1)! },
      { x: XAxis.length - 1, y: params.forecast!.avg },
      { x: XAxis.length - 1, y: params.forecast!.high },
    ];
    // 2. 使用 lineGenerator 生成路径数据，并在末尾添加 "Z" 来闭合路径，形成一个封闭的形状
    const highPathData = lineGenerator(highPoints) + "Z";

    // 3. 使用数据绑定模式来创建或更新 <path> 元素
    //    即使只有一个三角形，使用数据绑定也是 D3 的标准实践
    const highTriangle = forecastGroup
      .selectAll<SVGPathElement, null>(".high-triangle")
      .data([null]); // 绑定一个虚拟数据
    highTriangle.exit().transition(t).attr("opacity", 0).remove();
    const enterHighTriangle = highTriangle
      .enter()
      .append("path")
      .attr("class", "high-triangle")
      .attr("fill", "#02D79B")
      .attr("fill-opacity", 0.1)
      .attr("opacity", 0);
    highTriangle
      .merge(enterHighTriangle)
      .transition(t)
      .attr("d", highPathData)
      .attr("opacity", 1);

    // 下跌面积图
    const lowPoints = [
      { x: params.YAxis.data.length - 1, y: params.YAxis.data.at(-1)! },
      { x: XAxis.length - 1, y: params.forecast!.avg },
      { x: XAxis.length - 1, y: params.forecast!.low },
    ];
    // 2. 使用 lineGenerator 生成路径数据，并在末尾添加 "Z" 来闭合路径，形成一个封闭的形状
    const lowPathData = lineGenerator(lowPoints) + "Z";

    // 3. 使用数据绑定模式来创建或更新 <path> 元素
    //    即使只有一个三角形，使用数据绑定也是 D3 的标准实践
    const lowTriangle = forecastGroup
      .selectAll<SVGPathElement, null>(".low-triangle")
      .data([null]); // 绑定一个虚拟数据
    lowTriangle.exit().transition(t).attr("opacity", 0).remove();
    const enterLowTriangle = lowTriangle
      .enter()
      .append("path")
      .attr("class", "low-triangle")
      .attr("fill", "#FF7375")
      .attr("fill-opacity", 0.1)
      .attr("opacity", 0);
    lowTriangle
      .merge(enterLowTriangle)
      .transition(t)
      .attr("d", lowPathData)
      .attr("opacity", 1);
    // 绘制3个价格
    const data = forecastData;
    const priceGroup = el
      .selectAll<
        HTMLParagraphElement,
        { type: "low" | "avg" | "high"; value: number }
      >(".price")
      .data(data, (d) => d.type);

    priceGroup.exit().transition(t).style("opacity", 0).remove();

    const enterPriceGroup = priceGroup
      .enter()
      .append("p")
      .html((d) => d.html)
      .attr("class", (d) => {
        const className = forecastPriceClass;
        const high =
          "text-content_accent1_primary bg-background_accent1_overlay20";
        const avg =
          "text-content_secondary bg-background_white_overlay5 -translate-y-[50%]";
        const low =
          "text-content_accent2_primary bg-background_accent2_overlay20 -translate-y-[100%]";
        if (d.type === "high") {
          return className + " " + high;
        } else if (d.type === "avg") {
          return className + " " + avg;
        } else {
          return className + " " + low;
        }
      })
      .style("width", `${Math.floor(right - 14)}px`)
      .attr("opacity", "0");
    const topMap = {
      high: Math.floor(yScale(params.forecast?.high || 0)),
      avg: Math.floor(yScale(params.forecast?.avg || 0)),
      low: Math.floor(yScale(params.forecast?.low || 0)),
    };
    if (Math.abs(topMap.avg - topMap.high) < 32) {
      topMap.avg = topMap.high + 32;
    }
    if (Math.abs(topMap.avg - topMap.low) < 32) {
      topMap.low = topMap.avg + 32;
    }
    priceGroup
      .merge(enterPriceGroup)
      .transition(t)
      .style("top", (d) => `${topMap[d.type]}px`)
      .style("opacity", "1");
  };
  createDualYAxisLineChart(
    {
      ...params,
      config: {
        ...params.config,
        margin: {
          ...params.config.margin,
          right,
          bottom: 0,
        },
      },
      XAxis,
      ticks: {
        xKeyTicks: ticks,
      },
      YAxis: {
        left: [params.YAxis],
      },
      others: {
        extraYAxisData: params.forecast
          ? [params.forecast.low, params.forecast.avg, params.forecast.high]
          : undefined,
      },
    },
    drawEndCallback,
  );
};
